import { ObjectValues, Optional } from "./generic";
import { PaymentFrequency } from "./stripe";

export const SubscriptionStatus = {
  Active: "ACTIVE",
  PendingActivation: "PENDING_ACTIVATION",
  Expired: "EXPIRED",
  Suspended: "SUSPENDED",
  NotStarted: "NOT_STARTED",
} as const;
export type SubscriptionStatus = ObjectValues<typeof SubscriptionStatus>;

export const RenewalStatus = {
  Enabled: "ENABLED",
  Disabled: "DISABLED",
} as const;
export type RenewalStatus = ObjectValues<typeof RenewalStatus>;

export const PaymentStatus = {
  PendingPayment: "PENDING_PAYMENT",
  Paid: "PAID",
  Overdue: "OVERDUE",
} as const;
export type PaymentStatus = ObjectValues<typeof PaymentStatus>;

export const SubscriptionStatusLabel: Record<SubscriptionStatus, string> = {
  ACTIVE: "Active",
  EXPIRED: "Expired",
  PENDING_ACTIVATION: "Pending",
  SUSPENDED: "Suspended",
  NOT_STARTED: "Not Started",
};

export interface PlannerSubscription {
  Quota: number;
  QuotaUsage: number;
  TierID: string;
  Tier: string;
}

export interface DesignSubscription {
  Quota: number;
  QuotaUsage: number;
}

export interface BillingCycle {
  ID: string;
  StartTime: string;
  EndTime: string;
  BillingPeriod: PaymentFrequency | null;
  CreateTime: string;
  Status: SubscriptionStatus | null;
  LicenseCount: number;
  Planner: PlannerSubscription | null;
  Design: DesignSubscription | null;
  PaymentStatus: PaymentStatus;
}

export interface Subscription {
  ID: string;
  AccountID: string;
  StartTime: string;
  EndTime: string | null;
  BillingCycles: BillingCycle[];
  Status: SubscriptionStatus | null;
  RenewalStatus: RenewalStatus | null;
  PaymentStatus: PaymentStatus | null;
  DataPackQuota: number;
  DataPackUsage: number;
  DesignDataPackQuota: number;
  DesignDataPackUsage: number;
  /**
   * The Active planner package - the API does not return this,
   * we inject it into the store.
   *
   * TODO: Remove this and update code to instead reference BillingCycles
   */
  Design: DesignSubscription | null;
  Planner: PlannerSubscription | null;
  LicenseCount: number;
}

export type UsageItem = {
  Email: string;
  Usage: number;
};

export type DailyUsageItem = {
  Date: string;
  DailyUsageByAccount: number;
  DailyUsageByUser: UsageItem[];
};

export type QuotaUsageDetails = {
  // The total quota available
  AvailableQuota: number;
  // The amount of datapack quota that has ever been bought.
  // Note this is not renewed / reset on a billing cycle.
  DataPackQuota: number;
  // The amount of datapack quota that has ever been used
  // Note this is not renewed / reset on a billing cycle.
  DataPackUsage: number;
  // Base package quota available for the billing cycle
  Quota: number;
  // The amount of base package quota already used in the billing cycle
  QuotaUsage: number;
};

export type Usage = {
  StartDate: string;
  EndDate: string;
  TotalUsageByAccount: number;
  TotalUsageByUser: UsageItem[];
  DailyUsage: DailyUsageItem[];
};

// FIX: https://biarrinetworks.atlassian.net/browse/FND-3788
export type SubscriptionCreateRequest = Optional<
  Pick<Subscription, "AccountID" | "StartTime" | "LicenseCount" | "Planner"> & {
    Quota: number;
    QuotaUsage: number;
    SubscriptionTierID: string;
  },
  "EndTime"
>;
